import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/api";
import { setCurrency } from "@/currency";
import axios from "axios";
import { clearUndo } from "@/undo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: if returning from OAuth callback, let AuthCallback handle exchange first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    // Public kiosk/portal routes: skip auth check (avoids 401 noise)
    const path = window.location.pathname;
    if (/^\/(clock|turni|portal|doc|menu)\//.test(path) || path.startsWith("/sala")) { setLoading(false); return; }
    const token = localStorage.getItem("token");
    try {
      // Try cookie session first (Google), fall back to Bearer token
      const cfg = { withCredentials: true };
      if (token) cfg.headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/auth/me`, cfg);
      setUser(res.data);
      setCurrency(res.data.currency_symbol);
      setLoading(false);
    } catch (err) {
      const st = err?.response?.status;
      if (st === 401 || st === 403) {
        // Token really invalid/expired: clear it
        localStorage.removeItem("token");
        setUser(null);
        setLoading(false);
      } else if (token) {
        // Network hiccup / server restart: KEEP the session and retry
        setTimeout(() => { checkAuth(); }, 3000);
      } else {
        setUser(null);
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  useEffect(() => {
    const onStorage = (e) => { if (e.key === "token") checkAuth(); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [checkAuth]);

  const login = async (identifier, password) => {
    const res = await api.login({ identifier, password });
    if (res.role === "partner" || res.role === "waiter" || res.two_factor_required) return res;
    if (res.role === "staff") {
      if (!res.must_change_password) localStorage.setItem("token", res.token);
      return res;
    }
    localStorage.setItem("token", res.token);
    setUser(res.business);
    checkAuth();
    return res;
  };
  const complete2fa = async (challenge_id, code) => {
    const res = await api.verify2fa(challenge_id, code);
    localStorage.setItem("token", res.token);
    setUser(res.business);
    checkAuth();
    return res;
  };
  const register = async (business_name, password, type, email, lockPin) => {
    const ref = localStorage.getItem("cc_ref") || null;
    const res = await api.register({ business_name, password, type, email: email || null, ref, lock_pin: lockPin || null });
    localStorage.removeItem("cc_ref");
    localStorage.setItem("token", res.token);
    setUser(res.business);
    checkAuth();
    return res.business;
  };
  const setGoogleUser = (biz) => setUser(biz);
  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    localStorage.removeItem("token");
    clearUndo();
    setUser(null);
  };

  return <AuthCtx.Provider value={{ user, loading, login, complete2fa, register, logout, setGoogleUser, refresh: checkAuth }}>{children}</AuthCtx.Provider>;
}
export const useAuth = () => useContext(AuthCtx);
export const canSeeCosts = (u) => !u?.staff || u.staff.access_role === "admin" || !!u.staff.elevated || !!u.staff.show_costs;
export const useMaskedCosts = () => { const { user } = useAuth(); return !!user?.staff && !canSeeCosts(user); };
